import angular = require("angular");
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { ClientDetails, IndividualClientDetails, InstitutionClientDetails } from "@models/clients/clientDetailsModel";
import { extractErrorMessage } from "@common/utils/errorUtils";
import { clientNumberKeyPress, clientNumberPaste, watchClientNumber } from "@common/utils/clientNumberUtils";

export interface ClientTypeSelection {
  clientType: string;
}

class ClientTypeSelectionModalController implements angular.IComponentController {
  static $inject = ["clientCRMRepo", "notifyjs", "$scope"];

  public selectedClientType: string;
  public onCreateClient: (params: { clientNumber: string }) => void;
  public onCancel: () => void;

  // Form fields
  public clientNumber: string;
  public firstName: string;
  public lastName: string;
  public companyName: string;

  public isSaving: boolean = false;

  constructor(
    private clientCRMRepo: ClientCRMRepo,
    private notifyjs: any,
    private $scope: angular.IScope
  ) {}

  $onInit() {
    this.selectedClientType = "Individual";

    // Watch for client number changes to validate it doesn't start with 0
    this.$scope.$watch(() => this.clientNumber, (newValue: string, oldValue: string) => {
      if (newValue && newValue.startsWith('0')) {
        watchClientNumber(newValue, oldValue, { clientNumber: this.clientNumber }, this.notifyjs);
        this.clientNumber = oldValue || '';
      }
    });
  }

  public onKeyPress(event: KeyboardEvent): boolean {
    return clientNumberKeyPress(event, this.notifyjs);
  }

  public onPaste(event: ClipboardEvent): void {
    clientNumberPaste(event, this.notifyjs);
  }

  public createClient() {
    if (!this.validateForm()) {
      return;
    }

    this.isSaving = true;
    const newClient = this.initializeNewClient(this.selectedClientType);

    this.clientCRMRepo.saveClient(newClient)
      .then((result: any) => {
        this.notifyjs("Client created successfully", "success");

        const newClientNumber = result.data.clientNumber || this.clientNumber;

        if (this.onCreateClient) {
          this.onCreateClient({ clientNumber: newClientNumber });
        }
      })
      .catch((err) => {
        this.notifyjs("Failed to create client: " + extractErrorMessage(err), "error");
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  private validateForm(): boolean {
    if (!this.clientNumber) {
      this.notifyjs("Client Number is required", "error");
      return false;
    }

    if (this.selectedClientType === 'Individual') {
      if (!this.firstName) {
        this.notifyjs("First Name is required", "error");
        return false;
      }
      if (!this.lastName) {
        this.notifyjs("Last Name is required", "error");
        return false;
      }
    } else {
      if (!this.companyName) {
        this.notifyjs("Company Name is required", "error");
        return false;
      }
    }

    return true;
  }

  private initializeNewClient(clientType: string): ClientDetails {
    const baseClient = {
      id: 0,
      clientNumber: this.clientNumber,
      typeOfClient: 3,
      clientType: clientType,
      status: 'Active',
      reportingCurrency: 'USD',
      reportLanguage: 'English',
      portfolios: [],
      reportEmails: [],
      finYearStart: 1,
      dietz: 0,
      isSecondaryBenchmarkColumnEnabled: false,
      isLocalStrategyEnabled: false,
      isInternationalStrategyEnabled: false,
      isCombinedStrategyEnabled: false,
      portfolioReportingTitle: 0,
      portfolioReportingType: 0,
      portfolioModeOfCorrespondence: 0,
      portfolioFrequency: 0,
      portfolioPreferredLocation: 0
    };

    if (clientType === 'Individual') {
      return {
        ...baseClient,
        firstName: this.firstName,
        lastName: this.lastName,
        name: `${this.firstName} ${this.lastName}`,
        contactTitle: 0,
        geographicalLocation: 0,
        employmentStatus: 0,
        emergencyTypeOfRelatedParty: 0,
        numberOfChildren: 0,
        spouse: 0
      } as IndividualClientDetails;
    } else if (clientType === 'Institution') {
      return {
        ...baseClient,
        name: this.companyName,
        institutionBusinessRegistrationNumber: ''
      } as InstitutionClientDetails;
    } else {
      console.error('Invalid client type', clientType);
      return null;
    }
  }

  public cancel() {
    if (this.onCancel) {
      this.onCancel();
    }
  }
}

export const clientTypeSelectionModal: angular.IComponentOptions = {
  template: require("./client-type-selection-modal.component.html"),
  controller: ClientTypeSelectionModalController,
  bindings: {
    onCreateClient: "&",
    onCancel: "&"
  }
};



// WEBPACK FOOTER //
// ./src/app/clients/modals/client-type-selection-modal.component.ts